import {EditEntrieParams, useSchemeStore} from 'entities/apiaryScheme';
import {useApiariesStore} from 'entities/apiaries';
import {useDevicesStore} from 'entities/devices';
import {useHivesStore} from 'entities/hives';
import {useThemeStore} from 'entities/theme';
import {useUserStore} from 'entities/user';
import {markColors, MarkColorsType} from 'features/hives';
import Konva from 'konva';
import {Layer} from 'konva/lib/Layer';
import {Stage} from 'konva/lib/Stage';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useDebounceCallback} from 'shared/lib/debounce/useDebounce';

type StageSizes = {
  width: number;
  height: number;
};

export type SchemeHive = {
  id: string;
  number: number;
  year: number | null;
  mark: number | null;
  color: MarkColorsType;
  breed: string | null;
  devicesCount: number;
  x: number;
  y: number;
  textX: number;
  textY: number;
};

export const GRID_SIZE = window?.matchMedia?.('(width < 767px)')?.matches ? 70 : 100;
export const SCALES = [0.1, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
export const TEXT_PROPS: Konva.TextConfig = {
  fontFamily: '"Rostelecom Basis",Arial,Helvetica,sans-serif',
  fontSize: 16,
  lineHeight: 20 / 16,
  fontStyle: '700',
  fill: '#FF4F12',
  align: 'center',
};

export const useApiaryScheme = () => {
  const currentApiaryId = useApiariesStore((s) => s.currentApiaryId);
  const currentTheme = useThemeStore((s) => s.theme);
  const myInfo = useUserStore((s) => s.userInfo);
  const hives = useHivesStore((s) => s.hives);
  const isLoading = useHivesStore((s) => s.isLoading);
  const allDevices = useDevicesStore((s) => s.allDevices);
  const devicesApiaryId = useDevicesStore((s) => s.lastApiaryId);
  const getDevicesFromServer = useDevicesStore((s) => s.getDevicesFromServer);
  const getEntrie = useSchemeStore((s) => s.getEntrie);
  const editEntrie = useSchemeStore((s) => s.editEntrie);

  const canvasRef = useRef<Stage | null>(null);
  const layerRef = useRef<Layer | null>(null);
  const objectsRef = useRef<Layer | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const stageSizes = useRef<StageSizes>({width: 0, height: 0});
  const editArr = useRef<Pick<SchemeHive, 'id' | 'x' | 'y'>[]>([]);

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [hiveSchemeItems, setHiveSchemeItems] = useState<SchemeHive[]>([]);
  const [originalHiveSchemeItems, setOriginalHiveSchemeItems] = useState<SchemeHive[]>([]);
  const [activeSchemeItem, setActiveSchemeItem] = useState<SchemeHive | null>(null);
  const [activeSchemeItemCoords, setActiveSchemeItemCoords] = useState<{top: number; left: number}>(
    {top: 0, left: 0}
  );
  const [isActiveSchemeItemOpen, setIsActiveSchemeItemOpen] = useState<boolean>(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);

  const devicesCountByHiveId = useMemo<Record<string, number>>(() => {
    if (!currentApiaryId || !devicesApiaryId || String(currentApiaryId) !== String(devicesApiaryId))
      return {};

    return allDevices.reduce(
      (acc, device) => {
        if (!device.hiveId) return acc;

        const hiveId = String(device.hiveId);
        acc[hiveId] = (acc[hiveId] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [allDevices, currentApiaryId, devicesApiaryId]);

  // const setScale = (maxX: number) => {
  //   if (!canvasRef.current) return;

  //   const width = canvasRef.current.width();
  //   const scale = canvasRef.current.scale().x;
  //   const leftBorder = (maxX + GRID_SIZE) * scale;
  //   const currScaleIdx = SCALES.findIndex(el => el === scale);
  //   if (currScaleIdx === 0) return;

  //   if (leftBorder > width) {
  //     scaleDown();
  //     setScale(maxX);
  //   }
  // }

  const setCenter = (bounds: {maxX: number; maxY: number; minX: number; minY: number}) => {
    if (!canvasRef.current) return;
    const width = bounds.maxX - bounds.minX;
    const height = bounds.maxY - bounds.minY;

    const scaleX = canvasRef.current.width() / width;
    const scaleY = canvasRef.current.height() / height;

    let scale = Math.min(scaleX, scaleY) * 0.7;
    const maxScale = 1;
    if (scale > maxScale) scale = maxScale;
    canvasRef.current.scale({x: scale, y: scale});

    const canvasWidth = canvasRef.current.width();
    const canvasHeight = canvasRef.current.height();

    let pos = {
      x: canvasRef.current.width() / 2 - (bounds.minX + width / 2) * scale,
      y: canvasRef.current.height() / 4 - (bounds.minY + height / 2) * scale,
    };

    const resolution = canvasWidth / canvasHeight;
    if (resolution <= 1) {
      pos = {
        x: canvasRef.current.width() * 0.75 - (bounds.minX + width / 2) * scale,
        y: canvasRef.current.height() / 4 - (bounds.minY + height / 2) * scale,
      };
    }

    canvasRef.current.position(pos);

    drawGrid();
  };

  const editEntries = async () => {
    if (!editArr.current.length || !myInfo) return;

    for (let index = 0; index < editArr.current.length; index++) {
      const editItem = editArr.current[index];
      const params: EditEntrieParams = {
        userId: myInfo.id,
        entryId: editItem.id,
        data: {
          x: editItem.x,
          y: editItem.y,
        },
      };

      await editEntrie(params);
    }

    editArr.current = [];
  };

  const getHiveSchemeItems = useDebounceCallback(async () => {
    if (!hives.length) return setHiveSchemeItems([]);

    let minX = Infinity;
    let minY = Infinity;

    let maxX = 0;
    let maxY = 0;

    const arr = [];
    for (let index = 0; index < hives.length; index++) {
      const hive = hives[index];
      let coords = await getEntrie(`hive_${hive.id}`);

      if (!coords) {
        coords = {x: 0, y: 0};
        const id = `hive_${hive.id}`;
        const obj = {
          id,
          x: 0,
          y: 0,
        };
        const idx = editArr.current.findIndex((el) => el.id === id);
        if (idx >= 0) {
          editArr.current[idx] = obj;
        } else {
          editArr.current.push(obj);
        }
      }

      // eslint-disable-next-line prefer-const
      let {x, y} = coords;
      if (x === 0) {
        x = (GRID_SIZE / 2 + 10) * index;
      }

      if (index === 0) {
        maxX = x;
        minX = x;
        maxY = y;
        minY = y;
      }

      maxX = Math.max(maxX, x);
      minX = Math.min(maxX, x);
      maxY = Math.max(maxY, y);
      minY = Math.min(maxY, y);

      arr.push({
        id: hive.id,
        number: hive.number,
        year: hive.uterus.year,
        mark: hive.uterus.mark,
        color:
          markColors.find((el) => Number(el.id) === Number(hive.uterus.color)) || markColors[0],
        breed: hive.uterus.breed,
        devicesCount: devicesCountByHiveId[String(hive.id)] || 0,
        x: x || 0,
        y: y || 0,
        // textX: (x || 0) + (GRID_SIZE / 2) - (getTextWidth(`№${hive.number}`) / 2),
        textX: x || 0,
        textY: y || 0,
      });
    }

    const bounds = {
      maxX,
      maxY,
      minX,
      minY,
    };

    setCenter(bounds);
    setHiveSchemeItems(arr);
    editEntries();
  }, 300);

  const setStageSizes = () => {
    if (!wrapperRef.current || !canvasRef.current) return;

    const width = wrapperRef.current.offsetWidth;
    const height = wrapperRef.current.offsetHeight;

    canvasRef.current.width(width);
    canvasRef.current.height(height);
    drawGrid();
  };

  const scaleDown = () => {
    if (!canvasRef.current) return;

    // eslint-disable-next-line no-unsafe-optional-chaining
    const {x: currScale} = canvasRef.current?.scale();
    const currScaleIdx = SCALES.findIndex((el, index) => {
      let condition;
      if (index === SCALES.length - 1) {
        condition = currScale >= el;
      } else {
        condition = currScale >= el && currScale < SCALES[index + 1];
      }

      return condition;
    });
    if (currScaleIdx < 0) return;

    const newScale = SCALES[currScaleIdx - 1];
    if (!newScale) return;

    canvasRef.current.scale({x: newScale, y: newScale});
    drawGrid();
  };

  const scaleUp = () => {
    if (!canvasRef.current) return;

    // eslint-disable-next-line no-unsafe-optional-chaining
    const {x: currScale} = canvasRef.current?.scale();
    const currScaleIdx = SCALES.findIndex((el, index) => {
      let condition;
      if (index === SCALES.length - 1) {
        condition = currScale >= el;
      } else {
        condition = currScale >= el && currScale < SCALES[index + 1];
      }

      return condition;
    });
    if (currScaleIdx < 0) return;

    const newScale = SCALES[currScaleIdx + 1];
    if (!newScale) return;

    canvasRef.current.scale({x: newScale, y: newScale});
    drawGrid();
  };

  const drawGrid = () => {
    if (!layerRef.current || !canvasRef.current) return;

    const stroke = currentTheme === 'light' ? '#585D6940' : '#B5B7C040';
    layerRef.current.destroyChildren(); // Clear existing grid lines

    const stageX = canvasRef.current.x();
    const stageY = canvasRef.current.y();
    const scaleX = canvasRef.current.scaleX();
    const scaleY = canvasRef.current.scaleY();

    // Calculate visible area in world coordinates
    const visibleStartX = -stageX / scaleX;
    const visibleStartY = -stageY / scaleY;
    const visibleEndX = visibleStartX + canvasRef.current.width() / scaleX;
    const visibleEndY = visibleStartY + canvasRef.current.height() / scaleY;

    // Draw vertical lines
    for (
      let x = Math.floor(visibleStartX / GRID_SIZE) * GRID_SIZE;
      x < visibleEndX;
      x += GRID_SIZE
    ) {
      layerRef.current.add(
        new Konva.Line({
          points: [x, visibleStartY, x, visibleEndY],
          stroke,
          strokeWidth: 1,
        })
      );
    }

    // Draw horizontal lines
    for (
      let y = Math.floor(visibleStartY / GRID_SIZE) * GRID_SIZE;
      y < visibleEndY;
      y += GRID_SIZE
    ) {
      layerRef.current.add(
        new Konva.Line({
          points: [visibleStartX, y, visibleEndX, y],
          stroke,
          strokeWidth: 1,
        })
      );
    }

    layerRef.current.batchDraw();
  };

  const onCanvasWheel = (evt: Konva.KonvaEventObject<WheelEvent>) => {
    if (!canvasRef.current) return;

    const delta = evt.evt.deltaY;

    if (delta > 0) {
      scaleDown();
    } else {
      scaleUp();
    }
  };

  const onGroupClick = (
    evt: Konva.KonvaEventObject<Partial<MouseEvent> & Partial<TouchEvent>>,
    hive: SchemeHive
  ) => {
    if (isEditMode) return;
    const pageX = evt.evt.pageX || evt.evt.touches?.[0].pageX || 0;
    const pageY = evt.evt.pageY || evt.evt.touches?.[0].pageY || 0;

    const top = pageY;
    const left = pageX + 20;

    setActiveSchemeItemCoords({top, left});
    setActiveSchemeItem(hive);
    setIsActiveSchemeItemOpen(true);
  };

  const onActiveSchemeItemClose = () => {
    setActiveSchemeItem(null);
    setIsActiveSchemeItemOpen(false);
  };

  const onDragEnd = (evt: Konva.KonvaEventObject<DragEvent>, hive: SchemeHive) => {
    if (!isEditMode) return;
    const finded = hiveSchemeItems.find((el) => el.id === hive.id);
    if (!finded) return;

    const id = `hive_${hive.id}`;

    const {x: newX, y: newY} = evt.target.position();

    // Используем оригинальную логику расчета позиции
    const obj = {
      id,
      x: (newX + finded.x) / 2,
      y: (newY + finded.y) / 2,
    };

    const idx = editArr.current.findIndex((el) => el.id === id);
    if (idx >= 0) {
      editArr.current[idx] = obj;
    } else {
      editArr.current.push(obj);
    }
  };

  const onEditModeClick = () => {
    if (!isEditMode) {
      // Сохраняем исходные позиции при входе в режим редактирования
      setOriginalHiveSchemeItems([...hiveSchemeItems]);
      setIsEditMode(true);
      return;
    }

    setIsEditMode(false);
    editEntries();
  };

  const onCancelEdit = () => {
    // Проверяем, есть ли несохраненные изменения
    if (editArr.current.length > 0) {
      // Показываем модальное окно с предупреждением
      setIsCancelModalOpen(true);
    } else {
      // Если изменений нет, сразу отменяем
      handleCancelConfirm();
    }
  };

  const handleCancelConfirm = () => {
    // Возвращаем ульи на исходные позиции и выходим из режима редактирования
    if (originalHiveSchemeItems.length > 0) {
      // Обновляем состояние
      setHiveSchemeItems([...originalHiveSchemeItems]);

      // Принудительно обновляем позиции на canvas через refs
      if (objectsRef.current) {
        const children = objectsRef.current.getChildren();
        originalHiveSchemeItems.forEach((item) => {
          // Ищем Group по id
          const group = children.find((child: Konva.Node) => {
            return child.id() === item.id;
          }) as Konva.Group | undefined;

          if (group) {
            group.position({x: item.x, y: item.y});
          }
        });
        objectsRef.current.batchDraw();
      }
    }
    editArr.current = [];
    setIsEditMode(false);
    setIsCancelModalOpen(false);
  };

  useEffect(() => {
    getHiveSchemeItems();
  }, [hives, devicesCountByHiveId]);

  useEffect(() => {
    if (!currentApiaryId) return;
    getDevicesFromServer({apiaryId: currentApiaryId});
  }, [currentApiaryId, getDevicesFromServer]);

  useEffect(() => {
    if (wrapperRef.current && canvasRef.current) {
      setStageSizes();
      getHiveSchemeItems();
      window.addEventListener('resize', setStageSizes);
    }

    if (layerRef.current) drawGrid();

    return () => {
      window.removeEventListener('resize', setStageSizes);
    };
  }, [wrapperRef, canvasRef, layerRef]);

  const hasHives = hiveSchemeItems.length > 0;

  return {
    canvasRef,
    wrapperRef,
    stageSizes,
    layerRef,
    isEditMode,
    hiveSchemeItems,
    currentTheme,
    activeSchemeItem,
    activeSchemeItemCoords,
    isActiveSchemeItemOpen,
    isCancelModalOpen,
    isInfoModalOpen,
    objectsRef,
    onCanvasWheel,
    drawGrid,
    setIsEditMode,
    scaleDown,
    scaleUp,
    onGroupClick,
    onActiveSchemeItemClose,
    onDragEnd,
    onEditModeClick,
    onCancelEdit,
    handleCancelConfirm,
    setIsCancelModalOpen,
    setIsInfoModalOpen,
    hasHives,
    isLoading,
  };
};
